import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PiUser, PiNotePencil, PiDrop, PiFlask, PiPlant, PiImages, PiLightbulb, PiShieldCheck, PiCheck, PiDotsThree } from 'react-icons/pi';
import { istDayStart, listNames, clock } from '../../contexts/FarmStatusContext';
import { systemName } from '../../config/systems';
import { Panel, PanelTitle } from './ui';
import { IST, alpha, plural, hhmm24 } from './helpers';

/* Today as a timeline: time on the left, a rail with a dot, an icon disc, then
   what happened. Done = green check, still to do = amber, scheduled = blue info;
   things later today are faded. */
const Timeline = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Event = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: 52px 14px 30px minmax(0, 1fr);
  align-items: start;
  column-gap: 8px;
  padding-bottom: 14px;
  opacity: ${p => (p.$kind === 'future' ? 0.55 : 1)};
  &:last-child { padding-bottom: 0; }

  .time { padding-top: 6px; font-size: 12px; color: ${p => p.theme.colors.textMuted}; text-align: right; white-space: nowrap; }
  .time.due { color: ${p => p.theme.colors.warningHover}; font-weight: 500; }
  /* the rail: a line through every row but the last, with this row's dot on it */
  .rail { position: relative; align-self: stretch; }
  .rail::before {
    content: ''; position: absolute; left: 6px; top: 12px; bottom: -14px; width: 2px;
    background: ${p => p.theme.colors.border};
  }
  &:last-child .rail::before { display: none; }
  .rail i {
    position: absolute; left: 3px; top: 9px; width: 8px; height: 8px; border-radius: 50%;
    background: ${p => tone(p.theme, p.$kind)};
  }
  .disc {
    width: 28px; height: 28px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 15px;
    color: ${p => (p.$kind === 'done' ? '#fff' : tone(p.theme, p.$kind))};
    background: ${p => (p.$kind === 'done' ? p.theme.colors.success : alpha(tone(p.theme, p.$kind), 0.16))};
  }
  .what { padding-top: 2px; min-width: 0; }
  .what b { display: block; font-weight: 500; line-height: 1.3; color: ${p => p.theme.colors.text}; }
  .what span { display: block; margin-top: 1px; font-size: 13px; color: ${p => p.theme.colors.textSecondary}; }
`;

const EarlierButton = styled.button`
  min-height: 0;
  padding: 4px 0 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.theme.colors.primary};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

// How many of today's events show before the rest fold into "N earlier events".
const TODAY_RECENT = 4;

const tone = (theme, kind) => (kind === 'done' ? theme.colors.success
  : kind === 'todo' ? theme.colors.warning
    : kind === 'late' ? theme.colors.critical : theme.colors.water);

/* Today as a timeline: what has happened, what is still to do, and the next light switch. */
export default function TodayCard({ ctx, lights, tanks, now, isUnitEnabled, order }) {
  const [showEarlier, setShowEarlier] = useState(false);
  const todayStart = istDayStart(now);

  /* --- today, as a timeline ------------------------------------------- */
  const today = useMemo(() => {
    const ev = [];
    const at = (t, kind, Icon, title, detail) => ev.push({ t, kind, Icon, title, detail });
    const names = ids => listNames([...new Set(ids.map(systemName))]);
    const mgr = ctx.manager;
    if (mgr?.check_in) at(mgr.check_in, 'done', PiUser, 'Manager in', null);
    if (mgr?.check_out) at(mgr.check_out, 'done', PiUser, 'Manager out', mgr.check_in ? `${((mgr.check_out - mgr.check_in) / 3600).toFixed(1)} h on site` : null);

    // Tank readings today: one event per round (readings within 90 min of each other).
    const active = tanks.filter(t => !t.maintenance);
    const readToday = active.filter(t => t.checkedToday).sort((a, b) => a.lastAt - b.lastAt);
    const rounds = [];
    readToday.forEach((t) => {
      const r = rounds[rounds.length - 1];
      if (r && t.lastAt - r.end <= 5400) { r.tanks.push(t); r.end = t.lastAt; } else rounds.push({ start: t.lastAt, end: t.lastAt, tanks: [t] });
    });
    rounds.forEach((r, i) => {
      const last = i === rounds.length - 1;
      const left = active.filter(t => !t.checkedToday).map(t => t.name);
      at(r.start, 'done', PiNotePencil, last ? `Tanks checked ${readToday.length} of ${active.length}` : 'Tanks checked',
        last && left.length ? `${listNames(left)} left` : listNames(r.tanks.map(t => t.name)));
    });

    const doses = (ctx.doses || []).filter(d => d.timestamp >= todayStart);
    doses.forEach((d) => {
      const ml = Object.values(d.amounts || {}).reduce((x, v) => x + (Number(v) || 0), 0);
      if (d.is_water_change) at(d.timestamp, 'done', PiDrop, 'Water changed', systemName(d.system_id));
      else if (/^pH (Up|Down)$/.test(d.nutrient_name || '')) at(d.timestamp, 'done', PiFlask, 'pH corrected', `${systemName(d.system_id)}, ${d.nutrient_name} ${ml} ml`);
      else at(d.timestamp, 'done', PiFlask, 'Nutrients topped up', `${systemName(d.system_id)}, ${d.nutrient_name}`);
    });

    const logs = (ctx.logs || []).filter(l => l.timestamp >= todayStart);
    if (logs.length) {
      const photos = logs.reduce((x, l) => x + (l.images?.length || 0), 0);
      at(Math.min(...logs.map(l => l.timestamp)), 'done', PiPlant, 'Plant log',
        `${plural(logs.length, 'entry', 'entries')}${photos ? `, ${plural(photos, 'photo')}` : ''}`);
    }

    const key = new Date((now + IST) * 1000).toISOString().slice(0, 10);
    const photoDay = (ctx.rackPhotos || []).find(d => d.day === key);
    if (photoDay) {
      const first = Math.min(...photoDay.racks.flatMap(r => r.photos.map(p => p.uploaded_at)));
      at(first, 'done', PiImages, 'Rack photos', `${plural(photoDay.count, 'photo')}${photoDay.unsent === 0 ? `, sent ${clock(photoDay.notified_at, now)}` : ''}`);
    }

    // Lights from each rack's timer: racks switching together are one event.
    const switches = {};
    Object.values(lights?.units || {}).forEach((u) => {
      if (!isUnitEnabled(u.unit_id) || u.control_modes?.lights === 'manual') return;
      const w = u.schedule?.lights;
      if (!w?.on || !w?.off) return;
      [['on', w.on], ['off', w.off]].forEach(([what, hhmm]) => {
        const [h, m] = hhmm.split(':').map(Number);
        const ts = todayStart + h * 3600 + m * 60;
        (switches[`${what}|${ts}`] = switches[`${what}|${ts}`] || { what, ts, ids: [] }).ids.push(u.unit_id);
      });
    });
    // Same-direction switches within 90 min are one row ("Lights on, Aero Tower 05:30,
    // Rack A and Rack B 06:00"), so a staggered morning is not three rows.
    ['on', 'off'].forEach((what) => {
      const list = Object.values(switches).filter(x => x.what === what).sort((x, y) => x.ts - y.ts);
      const groups = [];
      list.forEach((x) => {
        const g = groups[groups.length - 1];
        if (g && x.ts - g[g.length - 1].ts <= 5400) g.push(x); else groups.push([x]);
      });
      groups.forEach((g) => {
        const t0 = g[0].ts;
        const detail = g.length === 1 ? `${names(g[0].ids)}, timer`
          : g.map(x => `${names(x.ids)} ${hhmm24(x.ts)}`).join(', ');
        ev.push({ t: t0, kind: t0 > now ? 'future' : 'info', Icon: PiLightbulb, title: `Lights ${what}`, detail });
      });
    });

    ev.sort((a, b) => a.t - b.t);

    // Still to do today: no time of its own, listed after what has happened.
    const todo = [];
    const due = (ctx.water?.schedule || []).filter(w => isUnitEnabled(w.system_id) && (w.is_overdue || w.days_until_due === 0));
    if (due.length) todo.push({ kind: due.some(w => w.is_overdue) ? 'late' : 'todo', Icon: PiDrop, title: 'Water change due', detail: names(due.map(w => w.system_id)) });
    const sprays = (ctx.sprays || []).filter(sp => (sp.is_overdue && sp.days_since_application != null
      && sp.days_since_application <= (sp.frequency_days || 0) * 2 + 7) || sp.days_until_due === 0);
    if (sprays.length) todo.push({ kind: 'todo', Icon: PiShieldCheck, title: 'Spray due', detail: listNames(sprays.map(sp => sp.name)) });
    if (!readToday.length && active.length) todo.push({ kind: 'todo', Icon: PiNotePencil, title: 'Tanks not checked yet', detail: `${active.length} tanks` });
    if (!mgr?.check_in) todo.push({ kind: 'todo', Icon: PiUser, title: 'Manager not checked in', detail: 'Check in on Manager Log' });
    if (!photoDay) todo.push({ kind: 'todo', Icon: PiImages, title: 'Rack photos', detail: 'None yet today' });
    else if (photoDay.unsent > 0) todo.push({ kind: 'todo', Icon: PiImages, title: 'Rack photos not sent', detail: `${plural(photoDay.unsent, 'new photo')}, press Send on Rack Photos` });

    // Past events first, then what is still to do, then the next timer. The panel
    // stays short however busy the day: only the last few events show, the rest
    // fold into an "earlier today" row, and only the next switch is listed.
    const past = ev.filter(e => e.kind !== 'future');
    const later = ev.filter(e => e.kind === 'future');
    return { past, todo, next: later.slice(0, 1) };
  }, [ctx.manager, ctx.doses, ctx.water, ctx.sprays, ctx.rackPhotos, ctx.logs, lights, tanks, todayStart, now, isUnitEnabled]);

  return (
    <Panel $order={order}>
      <PanelTitle>Today <Link to="/manager-log">Manager log</Link></PanelTitle>
      <Timeline>
        {today.past.length > TODAY_RECENT && !showEarlier && (
          <Event $kind="info" as="li">
            <span className="time" />
            <span className="rail" aria-hidden="true"><i /></span>
            <span className="disc" aria-hidden="true"><PiDotsThree /></span>
            <span className="what">
              <EarlierButton type="button" onClick={() => setShowEarlier(true)}>
                {plural(today.past.length - TODAY_RECENT, 'earlier event')} today
              </EarlierButton>
            </span>
          </Event>
        )}
        {[...(showEarlier ? today.past : today.past.slice(-TODAY_RECENT)), ...today.todo, ...today.next].map((e, i) => (
          <Event key={`${e.title}|${e.t || i}`} $kind={e.kind}>
            <span className={`time${e.t ? '' : ' due'}`}>{e.t ? hhmm24(e.t) : e.kind === 'late' ? 'Late' : 'To do'}</span>
            <span className="rail" aria-hidden="true"><i /></span>
            <span className="disc" aria-hidden="true">{e.kind === 'done' ? <PiCheck /> : <e.Icon />}</span>
            <span className="what"><b>{e.title}</b>{e.detail && <span>{e.detail}</span>}</span>
          </Event>
        ))}
      </Timeline>
    </Panel>
  );
}
